import { cx } from "../../lib/cx.js";
import { canFocus, focusFirst } from "./focus.js";
/* empty css            */
import { usePortalContainer } from "./PortalHost.js";
import { lockScroll } from "./scrollLock.js";
import { registerLayer } from "./stack.js";
import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { createPortal } from "react-dom";
//#region src/components/_overlay/Layer.tsx
var ParentLayerContext = createContext(null);
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/**
* The shared base of every overlay (Popover, Modal, Drawer, Dropdown, Tooltip, Toast):
* portals its children into the themed scope's host in a fixed-position root with the
* kind's z-index, joins the layer stack for top-down Escape and outside-press dismissal,
* and, when modal, makes the background inert, locks scrolling and traps focus. Focus
* returns to the trigger on close. Render it only while the overlay is open.
*/
function Layer({ kind, onDismiss, modal = false, trapFocus = modal, lockScroll: shouldLockScroll = modal, focusOnOpen = trapFocus, initialFocusRef, returnFocusRef, restoreFocus = true, dismissOnEscape = true, dismissOnOutsidePress = true, insideRefs, contentRef, container, className, style, ref, children, ...rest }) {
	const host = usePortalContainer();
	const target = container ?? host;
	const parent = useContext(ParentLayerContext);
	const [root, setRoot] = useState(null);
	const handle = useRef({
		entry: null,
		root: null
	});
	const latest = useRef({
		onDismiss,
		dismissOnEscape,
		dismissOnOutsidePress,
		insideRefs,
		contentRef,
		returnFocusRef
	});
	useLayoutEffect(() => {
		latest.current = {
			onDismiss,
			dismissOnEscape,
			dismissOnOutsidePress,
			insideRefs,
			contentRef,
			returnFocusRef
		};
	});
	const rootRef = useCallback((element) => {
		handle.current.root = element;
		setRoot(element);
		setRef(ref, element);
	}, [ref]);
	useLayoutEffect(() => {
		if (!root) return;
		const previouslyFocused = document.activeElement;
		const parentRoot = parent?.current.root;
		const parentZ = parentRoot ? parseInt(getComputedStyle(parentRoot).zIndex, 10) : NaN;
		if (!Number.isNaN(parentZ)) root.style.setProperty("--ds-layer-floor", String(parentZ + 1));
		const entry = {
			parent: () => parent?.current.entry ?? null,
			root,
			modal,
			trapFocus,
			content: () => latest.current.contentRef?.current ?? null,
			inside: () => latest.current.insideRefs?.map((inside) => inside.current) ?? [],
			dismiss: (reason) => {
				const { onDismiss, dismissOnEscape, dismissOnOutsidePress } = latest.current;
				const allowed = reason === "escape" ? dismissOnEscape : dismissOnOutsidePress;
				if (allowed) onDismiss?.(reason);
				return allowed;
			}
		};
		const self = handle.current;
		self.entry = entry;
		const unregister = registerLayer(entry);
		const unlockScroll = shouldLockScroll ? lockScroll() : null;
		if (focusOnOpen) focusFirst(root, initialFocusRef?.current);
		return () => {
			const focusWasInside = root.contains(document.activeElement);
			const focusIsLost = !document.activeElement || document.activeElement === document.body;
			unregister();
			unlockScroll?.();
			self.entry = null;
			const returnTo = latest.current.returnFocusRef?.current ?? previouslyFocused;
			if (restoreFocus && (trapFocus || focusWasInside || focusIsLost) && canFocus(returnTo)) returnTo.focus();
		};
	}, [root]);
	if (!target) return null;
	return createPortal(/* @__PURE__ */ jsx(ParentLayerContext.Provider, {
		value: handle,
		children: /* @__PURE__ */ jsx("div", {
			...rest,
			ref: rootRef,
			className: cx("ds-layer", `ds-layer--${kind}`, className),
			style,
			"data-ds-layer": kind,
			children
		})
	}), target);
}
//#endregion
export { Layer };

//# sourceMappingURL=Layer.js.map